import { z } from 'zod';

/**
 * Live update contract.
 *
 * Requirement: "the UI should update as other people do stuff." The mechanism is
 * deliberately minimal — the server broadcasts *what changed*, never the changed data.
 * Clients then refetch through the normal REST endpoint.
 *
 * That constraint is the important part. If the socket carried row data there would be two
 * code paths producing the same records (the endpoint and the broadcast), and they would
 * eventually disagree — usually in a way that only shows up for one user, once. Sending
 * only invalidation keeps a single source of truth, and makes a dropped socket a
 * *slowness* problem rather than a *correctness* problem.
 */

export const ENTITY_CHANNELS = [
  'customer',
  'job',
  'appointment',
  'quote',
  'invoice',
  'payment',
  'thread',
  'message',
  'time_entry',
  'call',
  'route',
  'calendar_event',
  /*
   * The field's own records. Each is a channel rather than a detail of `appointment` because
   * each outlives the visit that produced it: an asset and a hazard live on the dwelling, a
   * prospect on a place nobody has a job for yet.
   */
  'asset',
  'hazard',
  'reading',
  'capture',
  'prospect',
  'visit_request',
  /*
   * The team. Carried on its own channel because a change here is cosmetic-but-everywhere: a
   * colour or a name is drawn on the schedule board, on every visit card and in the search
   * results, so those all have to be told, and none of them is an appointment change.
   */
  'team',
] as const;
export type EntityChannel = (typeof ENTITY_CHANNELS)[number];

export const invalidationSchema = z.object({
  type: z.literal('invalidate'),
  tenantId: z.string().uuid(),
  entity: z.enum(ENTITY_CHANNELS),
  /** The specific row, when the change is scoped to one. */
  id: z.string().uuid().nullable(),
  /**
   * Related rows worth refreshing too: changing an invoice affects its job's rollup and
   * the customer's balance. Sent so the client does not need to model those relationships.
   */
  also: z
    .array(z.object({ entity: z.enum(ENTITY_CHANNELS), id: z.string().uuid().nullable() }))
    .default([]),
  /** Who caused it, so a client can skip echoing its own optimistic update. */
  originUserId: z.string().uuid().nullable(),
  /**
   * The capability a socket must hold to be told.
   *
   * Null — the normal case — means every socket in the tenant, which is safe precisely
   * because an invalidation carries no data: learning that "invoice X changed" tells you
   * nothing you could not already ask for, and the refetch is authorised normally.
   *
   * It stops being safe the moment the *fact of the change* is itself the information. A
   * technician's position and an open hazard are both like that, so they name an audience and
   * the hub filters on it.
   */
  audience: z.string().nullable().default(null),
  at: z.string().datetime(),
});
export type Invalidation = z.infer<typeof invalidationSchema>;

/** Live presence, used to show "Vanessa is also viewing this job". */
export const presenceSchema = z.object({
  type: z.literal('presence'),
  tenantId: z.string().uuid(),
  /** Opaque room key, e.g. `job:<uuid>`. */
  room: z.string(),
  users: z.array(z.object({ userId: z.string().uuid(), name: z.string(), color: z.string().nullable() })),
});
export type Presence = z.infer<typeof presenceSchema>;

/** A live phone call reaching the UI, so an inbound call can pop the customer's card. */
export const callEventSchema = z.object({
  type: z.literal('call'),
  tenantId: z.string().uuid(),
  callId: z.string().uuid(),
  state: z.enum(['ringing', 'answered', 'ended']),
  direction: z.enum(['inbound', 'outbound']),
  fromNumber: z.string(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
});
export type CallEvent = z.infer<typeof callEventSchema>;

/**
 * A notification reaching the tray.
 *
 * Sent only to the user it concerns. Carries enough to render the row immediately, because
 * a notification the user cannot read until a refetch completes is not a notification.
 */
export const notificationEventSchema = z.object({
  type: z.literal('notification'),
  tenantId: z.string().uuid(),
  id: z.string().uuid(),
  kind: z.string(),
  title: z.string(),
  body: z.string().nullable(),
  severity: z.enum(['info', 'action']),
  entity: z.string().nullable(),
  entityId: z.string().uuid().nullable(),
  actorName: z.string().nullable(),
  count: z.number().int().default(1),
  at: z.string().datetime(),
});
export type NotificationEvent = z.infer<typeof notificationEventSchema>;

/**
 * Where a technician is, published live to dispatch.
 *
 * The first event in the system that must be published **by capability** rather than to the
 * whole tenant: a colleague has no business watching a van, and a manager browsing last month
 * is exactly what `FIELD.md` says this must never become. Breadcrumbs are coarse on purpose;
 * `approximate` is set when the fix is worse than 100 m, and a client must draw it as an area
 * rather than as a point.
 */
export const positionEventSchema = z.object({
  type: z.literal('position'),
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
  name: z.string(),
  color: z.string().nullable(),
  latitude: z.number(),
  longitude: z.number(),
  accuracyM: z.number().int().nullable(),
  approximate: z.boolean(),
  /** The stop this fix was stamped at, when it is an arrival rather than a breadcrumb. */
  appointmentId: z.string().uuid().nullable(),
  /** Dispatch, and nobody else: holders of `job.assign`. */
  audience: z.string().default('job.assign'),
  at: z.string().datetime(),
});
export type PositionEvent = z.infer<typeof positionEventSchema>;

/**
 * A hazard, the second by-capability event and the one that must not wait for a poll.
 *
 * It reaches the office as an *action*, not an *info*: somebody has to decide whether to send
 * an electrician, warn the customer, or stop the next visit going out.
 */
export const hazardEventSchema = z.object({
  type: z.literal('hazard'),
  tenantId: z.string().uuid(),
  hazardId: z.string().uuid(),
  severityKind: z.enum(['do_not_operate', 'fix_before_next_service', 'advisory']),
  target: z.enum(['technician', 'customer']),
  appointmentId: z.string().uuid().nullable(),
  jobId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  placeLabel: z.string().nullable(),
  note: z.string().nullable(),
  reportedBy: z.string(),
  audience: z.string().default('job.read.all'),
  at: z.string().datetime(),
});
export type HazardEvent = z.infer<typeof hazardEventSchema>;

/**
 * A change to a technician's own day, sent to that technician.
 *
 * The five-minute heartbeat is too slow for a stop added at 10:02 for 10:30, and a
 * technician has no right to be surprised by their own schedule. Delivered to the user it
 * concerns rather than to the tenant, because it is addressed to a person.
 */
export const assignmentEventSchema = z.object({
  type: z.literal('assignment'),
  tenantId: z.string().uuid(),
  /** The technician this concerns. The hub delivers to their sockets only. */
  userId: z.string().uuid(),
  appointmentId: z.string().uuid(),
  change: z.enum(['added', 'moved', 'cancelled', 'note', 'unassigned']),
  /** Enough to render the banner before the sync that follows it lands. */
  startsAt: z.string().datetime().nullable(),
  customerName: z.string().nullable(),
  actorName: z.string().nullable(),
  at: z.string().datetime(),
});
export type AssignmentEvent = z.infer<typeof assignmentEventSchema>;

export const serverMessageSchema = z.discriminatedUnion('type', [
  notificationEventSchema,
  positionEventSchema,
  hazardEventSchema,
  assignmentEventSchema,
  invalidationSchema,
  presenceSchema,
  callEventSchema,
  z.object({ type: z.literal('pong'), at: z.string().datetime() }),
]);
export type ServerMessage = z.infer<typeof serverMessageSchema>;

export const clientMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ping') }),
  /** Join/leave presence rooms as the user navigates. */
  z.object({ type: z.literal('subscribe'), rooms: z.array(z.string()).max(20) }),
  z.object({ type: z.literal('unsubscribe'), rooms: z.array(z.string()).max(20) }),
]);
export type ClientMessage = z.infer<typeof clientMessageSchema>;

/** Redis pub/sub channel for a tenant. One channel per tenant keeps fan-out scoped. */
export const redisChannelFor = (tenantId: string): string => `monrdv:tenant:${tenantId}`;
